import { useEditController } from 'react-admin';
import { useClearAutoSave } from 'src/components/common/AutoSaveFormListener';
import { useClearAutoSaveOnUnmount } from './useClearAutoSaveOnUnmount';
import { getAutoSaveKey, AutoSaveResource } from 'src/constants/autoSave';

/**
 * Hook that provides auto-save utilities for edit forms
 *
 * @param { AutoSaveResource } resource - Resource from AUTOSAVE_RESOURCES constant
 * @returns Object with storage key and auto-save management functions
 *
 * @example
 * ```tsx
 * import { AUTOSAVE_RESOURCES } from 'src/constants/autoSave';
 *
 * const MyEditForm = () => {
 *   const { storageKey, clearAutoSave, markAsSubmitting } = useFormAutoSave(AUTOSAVE_RESOURCES.EMPLOYEE);
 *
 *   const onSuccess = () => {
 *     markAsSubmitting(); // Prevent clearing on success navigation
 *     clearAutoSave(); // Clear auto-saved data
 *   };
 *
 *   return (
 *     <Edit onSuccess={onSuccess}>
 *       <TabbedForm>
 *         {storageKey && <AutoSaveFormListener storageKey={storageKey} />}
 *         {/* form fields *\/}
 *       </TabbedForm>
 *     </Edit>
 *   );
 * };
 * ```
 */
export const useFormAutoSave = (resource: AutoSaveResource) => {
    const { record } = useEditController();

    // Create storage key only when record ID is available
    // Uses unified pattern: 'resource-form-123' (same as create: 'resource-form-new')
    const storageKey = record?.id ? getAutoSaveKey(resource, record.id) : '';

    const { clearAutoSave } = useClearAutoSave(storageKey);
    const { markAsSubmitting } = useClearAutoSaveOnUnmount(storageKey);

    return {
        storageKey,
        clearAutoSave,
        markAsSubmitting,
        record
    };
};

export default useFormAutoSave;
